import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Header, Modal, Icon, Button, Message, Dimmer, Loader } from 'semantic-ui-react';
import { useSearchParams } from 'react-router-dom';
import { ReactTable, LoadingWrapper } from '../common';
import { fetchWithAuth, getCSRFToken, useAuth } from '../auth';
import './Documents.css';

const getViewerUrl = (extension, url) => {
    if (extension === 'pdf') return `${url}#view=FitH`;
    if (extension === 'docx') return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    return url;
};

export const Documents = () => {
    const auth = useAuth();
    const [searchParams] = useSearchParams();
    const initialSearchTerm = searchParams.get('q') || '';
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerUrl, setViewerUrl] = useState('');
    const [viewerTitle, setViewerTitle] = useState('');
    const [viewerLoading, setViewerLoading] = useState(false);
    const [viewerError, setViewerError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [uploadSuccess, setUploadSuccess] = useState('');
    const [deleting, setDeleting] = useState(null);
    const fileInputRef = useRef(null);
    const iframeRef = useRef(null);
    const openDocumentAbortRef = useRef(null);

    const canManageDocuments = auth.userIsIccSupportTeam && !auth.isReadOnly;

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}documents/`,
                {
                    headers: { 'X-CSRFToken': getCSRFToken() },
                    credentials: 'include'
                }
            );

            if (!response.ok) throw new Error('Failed to fetch documents');

            const data = await response.json();
            setDocuments(data.documents || []);
        } catch (err) {
            setError('Failed to load documents. Please try again.');
            console.error('Error fetching documents:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            uploadDocument(file);
        }
    };

    const uploadDocument = async (file) => {
        const extension = file.name.split('.').pop().toLowerCase();
        
        if (!['pdf', 'docx'].includes(extension)) {
            setUploadError('Only .pdf and .docx files are allowed');
            return;
        }

        if (file.size > 50 * 1024 * 1024) {
            setUploadError('File size must be less than 50MB');
            return;
        }

        try {
            setUploading(true);
            setUploadError('');
            setUploadSuccess('');

            const formData = new FormData();
            formData.append('file', file);

            const response = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}documents/`,
                {
                    method: 'POST',
                    headers: { 'X-CSRFToken': getCSRFToken() },
                    credentials: 'include',
                    body: formData
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to upload document');
            }

            setUploadSuccess(`${file.name} uploaded successfully`);
            
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            
            await new Promise(resolve => setTimeout(resolve, 1000));
            await fetchDocuments();
            setTimeout(() => setUploadSuccess(''), 3000);
        } catch (err) {
            setUploadError(err.message || 'Failed to upload document');
            console.error('Error uploading document:', err);
        } finally {
            setUploading(false);
        }
    };

    const openDocument = useCallback(async (filename, extension, blobName) => {
        openDocumentAbortRef.current?.abort();
        const controller = new AbortController();
        openDocumentAbortRef.current = controller;

        setViewerTitle(filename);
        setViewerUrl('');
        setViewerError('');
        setViewerLoading(true);
        setViewerOpen(true);
        try {
            const response = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}blob/read-url/?blobName=${encodeURIComponent(blobName)}`,
                {
                    headers: { 'X-CSRFToken': getCSRFToken() },
                    credentials: 'include',
                    signal: controller.signal
                }
            );
            if (!response.ok) throw new Error('Failed to get document URL');
            const { sasUrl } = await response.json();
            setViewerUrl(getViewerUrl(extension, sasUrl));
        } catch (err) {
            if (err.name !== 'AbortError') {
                setViewerError('Failed to load document. Please try again.');
                console.error('Error fetching document URL:', err);
            }
        } finally {
            if (!controller.signal.aborted) {
                setViewerLoading(false);
            }
        }
    }, []);

    const closeViewer = useCallback(() => {
        setViewerOpen(false);
        setViewerError('');
    }, []);

    const toggleFullscreen = useCallback(() => {
        const el = iframeRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
            el.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
    }, []);

    const deleteDocument = async (filename) => {
        if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) return;

        try {
            setDeleting(filename);
            setUploadError('');

            const response = await fetchWithAuth(
                `${process.env.REACT_APP_ICC_TOOL_API_HOST}documents/?filename=${encodeURIComponent(filename)}`,
                {
                    method: 'DELETE',
                    headers: { 'X-CSRFToken': getCSRFToken() },
                    credentials: 'include'
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to delete document');
            }

            await fetchDocuments();
        } catch (err) {
            setUploadError(err.message || 'Failed to delete document');
            console.error('Error deleting document:', err);
        } finally {
            setDeleting(null);
        }
    };

    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Document Name',
                accessor: 'filename',
                Cell: ({ row }) => (
                    <span
                        onClick={() => openDocument(row.original.filename, row.original.extension, row.original.name)}
                        className="document-name-link"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openDocument(row.original.filename, row.original.extension, row.original.name);
                            }
                        }}
                    >
                        {row.original.filename}
                    </span>
                )
            }
        ];

        if (canManageDocuments) {
            cols.push({
                Header: 'Actions',
                accessor: 'actions',
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ row }) => (
                    <Button
                        size="mini"
                        color="red"
                        icon
                        onClick={(e) => {
                            e.stopPropagation();
                            deleteDocument(row.original.filename);
                        }}
                        loading={deleting === row.original.filename}
                        disabled={deleting === row.original.filename}
                        title="Delete document"
                    >
                        <Icon name="trash" />
                    </Button>
                )
            });
        }

        return cols;
    }, [openDocument, deleteDocument, deleting, canManageDocuments]);

    if (error) {
        return (
            <div className="documents">
                <Header as='h2'>Documents</Header>
                <div className="documents-fetch-error">{error}</div>
            </div>
        );
    }

    return (
        <div className="documents">
            <Header as='h2'>Documents</Header>

            {uploadError && (
                <Message negative onDismiss={() => setUploadError('')}>
                    <Message.Header>Upload Error</Message.Header>
                    <p>{uploadError}</p>
                </Message>
            )}

            {uploadSuccess && (
                <Message positive onDismiss={() => setUploadSuccess('')}>
                    <Message.Header>Upload Successful</Message.Header>
                    <p>{uploadSuccess}</p>
                </Message>
            )}

            {canManageDocuments && (
                <div className="documents-upload-area">
                    <Button
                        primary
                        icon
                        labelPosition='left'
                        onClick={() => fileInputRef.current?.click()}
                        loading={uploading}
                        disabled={uploading}
                    >
                        <Icon name='upload' />
                        Upload Document
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.docx"
                        className="documents-file-input"
                        onChange={handleFileSelect}
                        aria-label="Upload document file"
                    />
                </div>
            )}

            <LoadingWrapper loading={loading} text="Loading documents">
                <ReactTable
                    key={initialSearchTerm}
                    className="channel-table react-table-mobile-compact"
                    data={documents}
                    columns={columns}
                    hideGlobalFilter={false}
                    initialGlobalFilter={initialSearchTerm || undefined}
                />
            </LoadingWrapper>

            <Modal open={viewerOpen} onClose={closeViewer} size='fullscreen'>
                <Modal.Header>
                    <div className="documents-modal-header">
                        <span>{viewerTitle}</span>
                        <div>
                            <Icon
                                name="expand arrows alternate"
                                className="documents-modal-expand-icon"
                                onClick={toggleFullscreen}
                                aria-label="Toggle fullscreen"
                                role="button"
                                tabIndex={0}
                            />
                            <Icon
                                name="close"
                                className="documents-modal-close-icon"
                                onClick={closeViewer}
                                aria-label="Close"
                                role="button"
                                tabIndex={0}
                            />
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Content className="documents-modal-content">
                    {viewerLoading && (
                        <Dimmer active inverted>
                            <Loader>Loading document...</Loader>
                        </Dimmer>
                    )}
                    {viewerError ? (
                        <div className="documents-viewer-error">{viewerError}</div>
                    ) : (
                        <iframe
                            ref={iframeRef}
                            src={viewerUrl}
                            className="documents-iframe"
                            title={viewerTitle}
                        />
                    )}
                </Modal.Content>
            </Modal>
        </div>
    );
}